import { useEffect, useState, useRef } from "react"
import useWindowSize from "hooks/useWindowSize"

interface Props {
  hash: string,
  params: any
}

const Sketch = ({ hash, params }: Props) => {
  const [width, setWidth] = useState(0)
  const elementRef = useRef(document.createElement("div"))
  const windowSize = useWindowSize()
  let source = `
<!DOCTYPE HTML>
<html lang='en'>
  <head>
    <script>let hash='${hash}'</script>
    <meta charset='utf-8'>
    <style type='text/css'>
      html {
        cursor: crosshair;
      }
      body {
        margin: 0;
        padding: 0;
      }
      canvas {
        padding: 0;
        margin: auto;
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }
    </style>
  </head>
  <script src=https://cdnjs.cloudflare.com/ajax/libs/p5.js/1.0.0/p5.min.js></script>
  <script>let params=${JSON.stringify(params)}</script>
  <script>
window.p5 = p5
p5.disableFriendlyErrors = true

function hashpairs(hash) {
  let p = []
  for (let i=0; i < 64; i+=2) {
    p.push(hash.slice(i+2, i+4))
  }
  return p.map(x => {return (parseInt(x, 16) % 100) / 100})
}

class Random {
  constructor(hash) {
    this.hash = hash
    this.hashpairs = hashpairs(hash)
    this.i = 0
  }
  increment() {
    if (this.i == this.hashpairs.length-1) {
      this.i = 0
    } else {
      this.i += 1
    }
  }
  rnd_dec() {
    let x = this.hashpairs[this.i]
    this.increment()
    return x
  }
  rnd_num(a=0, b=1) {
    return a+(b-a)*this.rnd_dec()
  }
  rnd_int(a=0, b=1) {
    return Math.floor(this.rnd_num(a, b+1))
  }
  rnd_arr(x) {
    return x[this.rnd_int(0, x.length-1)]
  }
}

function rescale(z, x1, x2, y1, y2) {
  if (z <= x1) {
    return y1
  }
  if (z >= x2) {
    return y2
  }
  return y1+(y2-y1)*((z-x1)/(x2-x1))
}

function Ship(
  mod1=0.5,
  mod2=0.5,
  mod3=0.5,
  mod4=1.0,
  color1='#FFFFFF',
  color2='#FFFFFF',
  color3='#FFFFFF',
  color4='#FFFFFF'
) {

  function tile(cx, cy, h, w, fade) {
    let t = []
    let v = 0
    for (let y1=0, yi=0; yi < cy; yi++) {
      let y2 = y1+R.rnd_arr(h)
      for (let x1=0, xi=0; xi < cx; xi++) {
        let x2 = x1+R.rnd_arr(w)
        if (fade) {
          v = rescale(xi+yi, 0, cx+cy, 0, 1)
        }
        if (R.rnd_num() > v) {
          let c = R.rnd_arr([color1, color2, color3, color4])
          t.push({x1:x1, y1:y1, x2:x2, y2:y2, c:c})
        }
        x1 = x2
      }
      y1 = y2
    }
    return t
  }

  function prtp(obj, rot, tx, ty, rev) {
    push()
    rotate(rot)
    translate(tx, ty)
    for (let o of obj) {
      let dx = o.x2-o.x1
      let dy = o.y2-o.y1
      fill(o.c)
      if (rev) {
        rect(o.y1, o.x1, dy, dx)
      } else {
        rect(o.x1, o.y1, dx, dy)
      }
    }
    pop()
  }

  function drw(obj, x, y) {
    prtp(obj, 0, x, y, false)
    prtp(obj, PI/2, y, -x, true)
  }
  
  let blaster_x = R.rnd_int(1, 2)
  let blaster_y = 10
  let blaster_h = [n(25), n(50)]
  let blaster_w = [n(10), n(25)]
  let blaster_fade = false
  let blaster_spread = n(rescale(mod3, 0, 1, -100, 100))
  let blaster_extend = n(rescale(mod4, 0, 1, -300, 0))
  let blaster = tile(blaster_x, blaster_y, blaster_h, blaster_w, blaster_fade)  
  let blaster_1_px = n(400)-blaster_spread
  let blaster_1_py = n(150)-blaster_extend
  let blaster_2_px = n(600)+blaster_spread
  let blaster_2_py = n(150)-blaster_extend

  let ship_x = mod1 < 0.3 ? 3 : Math.floor(mod1*10)
  let ship_y = mod2 < 0.3 ? 3 : Math.floor(mod2*10)
  let ship_hw = R.rnd_arr([[n(25), n(50)], [n(50), n(100)], [n(25), n(50), n(100)]])
  let ship_fade = true
  let ship = tile(ship_x, ship_y, ship_hw, ship_hw, ship_fade)
  let ship_px = n(500)
  let ship_py = n(375)

  background(0)
  stroke(0)
  strokeCap(SQUARE)
  strokeWeight(n(3))
  drw(blaster, blaster_1_px, blaster_1_py)
  drw(blaster, blaster_2_px, blaster_2_py)
  drw(ship, ship_px, ship_py)
}

let DIM = Math.min(window.innerWidth, window.innerHeight)
let R = new Random(hash)
function n(x) {
  return x*DIM/1000
}

function setup() {
  createCanvas(DIM, DIM)
}

function draw() {
  Ship(
    params.mod1,
    params.mod2,
    params.mod3,
    params.mod4,
    params.color1,
    params.color2,
    params.color3,
    params.color4
  )
  noLoop()
}
  </script>
  <body></body>
</html>`

  useEffect(() => {
    setWidth(elementRef.current.offsetWidth)
  }, [windowSize.width])

  let size = Math.floor(width)+"px"

  return (
    <div ref={elementRef}>
      <iframe 
        key={size} 
        width={size} 
        height={size} 
        frameBorder="0" 
        scrolling="no" 
        srcDoc={source}
      />
    </div>
  )
}

export default Sketch
